import api from '@/http/api';
import { useSocketStore } from '@/socketStore';
import { useAuthStore } from '@/store';
import { useMutation, useQuery } from '@tanstack/react-query'
import ProductDetail from '@univdiam/ui/customComponents/CatalogProduct/ProductDetail';
import { useCallback, useEffect, useMemo, useState } from 'react'
import { normalizeUrl } from '@univdiam/ui/lib/UrlNormalize'
import { shareUrl } from '@univdiam/ui/lib/shareUrl'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner';

interface ProductData {
  title: string[];
  setting_style: string[];
  diamond_count: number[];
  dimensions: string[];
  item_weight: string[];
  metal_type: string[];
  diameter: string[];
  carat_weight: string[];
  diamond_quality: string[];
  variant: string[];
}

type FilterType = {
  diameter: string;
  caratWeight: string;
  diamondQuality: string;
  selectedMetalId: string
  price?: string
  stock_id?: string
  quantity?: number
}

type CreateCatalogOrder = {
  job_title: string,
  product_type: string,
  product_name: string,
  quantity: string
  metal_type: string,
  special_instructions: string,
  media_attachment: string[],
  caret_weight: string
  diamond_quality: string
}


const getProduct = (type: string) => {
  return api.get(`/catalog/${type}`);
}

const orderCreation = (data: CreateCatalogOrder) => {
  return api.post(`/order/create`, { ...data, order_type: 'catalog' });
}



const CatalogProduct = () => {
  const { type } = useParams()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [filterdValue, setFilterdValue] = useState<FilterType | null>(null)
  const { data } = useQuery({
    queryKey: ["catalog-product", type],
    queryFn: async () => {
      return await getProduct(type!);
    },
    enabled: !!type
  });
  const actualProducts = data?.data?.product
  const filters = data?.data?.filters as ProductData

  // console.log('actualProducts', actualProducts)
  // console.log('filters', filters)

  const selectedProduct: FilterType = useMemo(() => {
    if (!filterdValue) return null;

    return actualProducts?.find((product: any) => {
      return (
        (!filterdValue.diameter || product.diameter === filterdValue.diameter) &&
        (!filterdValue.caratWeight ||
          product.carat_weight?.toString() === filterdValue.caratWeight) &&
        (!filterdValue.diamondQuality ||
          product.diamond_quality === filterdValue.diamondQuality) &&
        (!filterdValue.selectedMetalId ||
          product.metal_type === filterdValue.selectedMetalId)
      );
    });
  }, [actualProducts, filterdValue]);

  // Parse product images from the selected product's media_attachment,
  // falling back to the first product that has images
  const productImages = useMemo(() => {
    const product = selectedProduct ?? actualProducts?.find((p: any) => p.media_attachment)
    if (!product?.media_attachment) return undefined
    try {
      const urls = typeof product.media_attachment === 'string'
        ? JSON.parse(product.media_attachment)
        : product.media_attachment
      if (!Array.isArray(urls) || urls.length === 0) return undefined
      return urls.map((url: string, i: number) => ({
        src: url,
        alt: `Product image ${i + 1}`,
        // Small thumbs uploaded by seed script at catalog/thumbs/<filename>
        thumbnail: url.toLowerCase().endsWith('.mp4') ? url : url.replace('/catalog/', '/catalog/thumbs/'),
      }))
    } catch {
      return undefined
    }
  }, [selectedProduct, actualProducts]);

  // Preload every variant's images once, so switching metal/config swaps
  // instantly from browser cache instead of fetching at click time
  useEffect(() => {
    if (!actualProducts?.length) return
    const urls = new Set<string>()
    for (const p of actualProducts) {
      if (!p?.media_attachment) continue
      try {
        const arr = typeof p.media_attachment === 'string'
          ? JSON.parse(p.media_attachment)
          : p.media_attachment
        if (Array.isArray(arr)) {
          arr.forEach((u: string) => {
            if (typeof u === 'string' && !u.toLowerCase().endsWith('.mp4')) urls.add(normalizeUrl(u))
          })
        }
      } catch { /* ignore malformed media_attachment */ }
    }
    urls.forEach(u => {
      const img = new Image()
      img.decoding = 'async'
      img.src = u
    })
  }, [actualProducts])

  const sendNotification = useSocketStore((s) => s.sendNotification)

  const { mutate, isPending } = useMutation({
    mutationFn: (values: CreateCatalogOrder) => {
      console.log('create_order', values)
      return orderCreation(values)
    },
    onSuccess: async ({ data }) => {
      toast.success('Order created successfully!', {
        position: 'top-center'
      })

      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.customer_id.toString()!,
        action_type: 'order_created',
        order_id: data.order.id,
        title: `Order #${data.order.order_id} created`,
        metadata: {
          order_ref: data.order.id,
          order_id: data.order.order_id,
          customer_id: user?.customer_id.toString()!,
          customer_name: user?.full_name!,
          job_title: data.order.job_title
        },
      })
    }
  })


  const handleCreateOrder = (values: CreateCatalogOrder) => {
    mutate(values)
  }

  const generateCatalogShareLink = useCallback(async (params: { stock_id: number, share_data: Record<string, any> }) => {
    const res = await api.post('/catalog/share', params)
    const shareCode = res?.data?.shareCode ?? res?.data?.token
    if (!shareCode) return null
    return shareUrl('share_catalog', shareCode, user?.business_name)
  }, [user])

  const handleBack = () => {
    navigate(-1)
  }
  return (
    <div>
      <ProductDetail productImages={productImages} filters={filters} from='catalog' filterdValue={filterdValue!} setFilterdValue={setFilterdValue!} selectedProduct={selectedProduct} handleCreateOrder={handleCreateOrder} type={type!} isPending={isPending} handleBack={handleBack} actualProducts={actualProducts} generateCatalogShareLink={generateCatalogShareLink} />
    </div>
  )
}

export default CatalogProduct
